import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SoftphoneProvider, useSoftphone } from '@dialstack/sdk-react/softphone';
import { UserStateContext } from './user-state-context';
import { createCinchPhonesSoftphoneToken, getCinchPhonesCallers } from 'shared/cinch-phones.api';
export const SoftphoneContext = createContext();

/**
 * Lifts the SDK's `placeCall` out to this context.
 *
 * `useSoftphone()` throws outside `<SoftphoneProvider>`, and the provider only exists once
 * a phone is connected — so nothing outside can call the hook directly. This renders inside
 * the provider and parks the function on a ref, letting anywhere in the app dial through
 * the softphone without knowing whether it happens to be connected.
 */
const PlaceCallBridge = ({
  placeCallRef
}) => {
  const {
    placeCall
  } = useSoftphone();

  // A ref rather than state: this changes identity on the provider's renders, and storing
  // it in state here would loop.
  placeCallRef.current = placeCall;
  return null;
};

/**
 * Lifts the SDK's ringing calls out to this context, the same way PlaceCallBridge does
 * for dialing, so the call pop can show and answer them from outside the provider.
 *
 * Only the id and caller are copied into state; the live `Call` objects stay on a ref.
 * The SDK hands back a new array on its own renders, so comparing ids is what stops this
 * from setting state on every render.
 */
const IncomingCallsBridge = ({
  onChange,
  callsRef,
  answerRef
}) => {
  const {
    incomingCalls,
    answerCall
  } = useSoftphone();
  const ids = (incomingCalls || []).map(call => call.id).join(',');
  callsRef.current = incomingCalls || [];
  // The SDK's answerCall rather than call.answer(): it also holds whatever call is up.
  answerRef.current = answerCall;
  useEffect(() => {
    onChange((incomingCalls || []).map(call => ({
      id: call.id,
      from: call.from,
      fromName: call.fromName
    })));
    // Keyed on the ids on purpose; see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, onChange]);

  // Nothing is ringing once the provider goes away.
  useEffect(() => () => onChange([]), [onChange]);
  return null;
};

/** Which phone this browser last signed in as, per company. */
const storageKey = companyId => `cinch-phones.softphone.${companyId}`;
const readStoredPhone = companyId => {
  try {
    return window.localStorage.getItem(storageKey(companyId));
  } catch (e) {
    // Private windows and blocked site data throw rather than returning null.
    return null;
  }
};
const storePhone = (companyId, phoneId) => {
  try {
    if (phoneId) {
      window.localStorage.setItem(storageKey(companyId), phoneId);
    } else {
      window.localStorage.removeItem(storageKey(companyId));
    }
  } catch (e) {
    // Not being able to remember the choice is not worth breaking the phone over.
  }
};

/**
 * Owns the browser softphone's connection.
 *
 * The connection lives here rather than in the panel so it survives the panel being
 * opened and closed — a softphone that only receives calls while its window is open would
 * miss most of them. The panel below just renders the SDK's UI into this connection.
 *
 * These are shared business phones, so the user chooses which one this browser signs in
 * as. That choice is remembered per company; the backend still re-checks it every time a
 * token is minted, so remembering it can't grant access to a phone they lost.
 */
export const SoftphoneContextProvider = ({
  children
}) => {
  const {
    asCompany,
    hasIntegration
  } = useContext(UserStateContext);
  const [phones, setPhones] = useState([]);
  const [phone, setPhone] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const placeCallRef = useRef(null);
  const [incomingCalls, setIncomingCalls] = useState([]);
  const incomingCallsRef = useRef([]);
  const answerCallRef = useRef(null);
  const enabled = !!asCompany?.id && hasIntegration('cinch_phones');

  // The phones this user may sign in as. Also what the picker offers.
  useEffect(() => {
    if (!enabled) {
      setPhones([]);
      setPhone(null);
      setSession(null);
      return;
    }
    let cancelled = false;
    getCinchPhonesCallers({
      company: asCompany.id
    }).then(({
      data
    }) => {
      if (cancelled) return;
      const results = data?.results || [];
      setPhones(results);

      // Only reconnect to a phone they explicitly connected before, and only if it's
      // still one they may use. Nothing is auto-selected: connecting makes this browser
      // start ringing, which should never happen without being asked for.
      const remembered = readStoredPhone(asCompany.id);
      setPhone(results.find(entry => entry.id === remembered) || null);
    }).catch(err => {
      console.error('Error loading Cinch Phones softphone phones:', err);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, asCompany?.id]);
  const mintToken = useCallback(phoneId => createCinchPhonesSoftphoneToken({
    company: asCompany.id,
    user: phoneId
  }).then(({
    data
  }) => data), [asCompany?.id]);

  // Sign in whenever the chosen phone changes.
  useEffect(() => {
    if (!enabled || !phone?.id) {
      setSession(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    mintToken(phone.id).then(data => {
      if (!cancelled) setSession(data);
    }).catch(err => {
      console.error('Error signing in to the softphone:', err);
      if (!cancelled) setError('Unable to connect that phone. Please try again.');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, phone?.id, mintToken]);
  const choosePhone = useCallback(phoneId => {
    const match = phones.find(entry => entry.id === phoneId) || null;
    storePhone(asCompany?.id, match?.id || null);
    setPhone(match);
  }, [phones, asCompany?.id]);

  /**
   * Hang up the browser's registration.
   *
   * Clearing the session unmounts SoftphoneProvider, which tears the phone down - so this
   * browser stops ringing for that location. The choice is forgotten too, so a reload
   * stays off until someone connects again.
   */
  const disconnect = useCallback(() => {
    storePhone(asCompany?.id, null);
    setPhone(null);
    setSession(null);
    setError(null);
    placeCallRef.current = null;
  }, [asCompany?.id]);

  /** Dial from this browser. Only meaningful while `connected`. */
  const placeCall = useCallback(destination => {
    if (!placeCallRef.current) {
      return Promise.reject(new Error('The softphone is not connected.'));
    }
    return Promise.resolve(placeCallRef.current(destination));
  }, []);

  /** Answer or decline one of `incomingCalls`, by id. No-ops if it has stopped ringing. */
  const answerCall = useCallback(callId => {
    const call = incomingCallsRef.current.find(entry => entry.id === callId);
    if (call && answerCallRef.current) answerCallRef.current(call);
  }, []);
  const declineCall = useCallback(callId => {
    const call = incomingCallsRef.current.find(entry => entry.id === callId);
    if (call) call.reject('decline');
  }, []);

  // The SDK asks for a fresh token about a minute before the current one expires and
  // delivers it in-band, so the connection and any live call are untouched.
  const handleTokenExpiring = useCallback(() => mintToken(phone.id).then(data => data.token), [mintToken, phone?.id]);
  const value = useMemo(() => ({
    enabled,
    phones,
    phone,
    choosePhone,
    disconnect,
    placeCall,
    incomingCalls,
    answerCall,
    declineCall,
    connected: !!session,
    loading,
    error,
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen(current => !current)
  }), [enabled, phones, phone, choosePhone, disconnect, placeCall, incomingCalls, answerCall, declineCall, session, loading, error, isOpen]);
  if (!enabled || !session) {
    return <SoftphoneContext.Provider value={value}>{children}</SoftphoneContext.Provider>;
  }
  return <SoftphoneContext.Provider value={value}>
      <SoftphoneProvider token={session.token} apiBaseUrl={session.api_base_url} onTokenExpiring={handleTokenExpiring} onError={event => console.error('Softphone error:', event)}>
        <PlaceCallBridge placeCallRef={placeCallRef} />
        <IncomingCallsBridge onChange={setIncomingCalls} callsRef={incomingCallsRef} answerRef={answerCallRef} />
        {children}
      </SoftphoneProvider>
    </SoftphoneContext.Provider>;
};
export default SoftphoneContextProvider;